// @author jonathan.ronsten@svenskaspel.se

import { CollectionError } from '../../../error/CollectionError.js';
import { type ClientErrorDetails, getClientErrorDetails } from '../../../error/getClientErrorDetails.js';
import { type PaymentInfoType } from '../../../payment/createPaymentInfo.js';
import type { Wager } from '../../../wager/Wager.js';
import { getProductsString, hasLongSessionRestriction, numerous } from '../utils/utils.js';

/**
 * Function to handle error message for client.
 *
 * @param paymentError The payment error for transaction.
 * @param paymentInfo The payment info for transaction.
 * @param unpaidWagers The unpaid wagers in transaction.
 * @returns The error message to show for customer.
 */
export async function getErrorMessage(
  paymentError: CollectionError | Error,
  paymentInfo: PaymentInfoType,
  unpaidWagers?: readonly Wager[]
): Promise<ClientErrorDetails> {
  if (paymentError instanceof CollectionError) {
    const totalUnpaid = unpaidWagers?.length ?? 0;
    const unpaidProducts = getProductsString(paymentInfo, unpaidWagers);
    const baseError = paymentError.getFirstBaseError();

    if (!baseError) {
      return getClientErrorDetails('GENERIC');
    }

    if (baseError.clientCode === 'USER_HAS_EXCLUSION') {
      const longSessionRestrictionMsg = await hasLongSessionRestriction('pools');

      if (longSessionRestrictionMsg) {
        return longSessionRestrictionMsg;
      }
    }

    // TODO: generic becomes <any> and should be taken care of in a proper way
    const clientErrorObject = getClientErrorDetails(baseError.clientCode);

    const errorMessage = numerous(totalUnpaid)(['Ditt', 'Dina'])(`{#0} spel på ${unpaidProducts} gick inte igenom`);
    const messages = [clientErrorObject.message, errorMessage];

    return {
      title: clientErrorObject.title,
      message: messages.join(' '),
    };
  }

  return getClientErrorDetails('GENERIC');
}
